import * as actions_admin from 'actions/admin-actions';
import * as actions_attachments from 'actions/attachments-actions';
import * as toolbox from 'components/common/toolbox';
import AttachementGallery from 'components/attachments/attachment-gallery';
import AttachmentForm from 'components/attachments/attachment-form/attachment-form';
import Authorize from 'components/common/authorize';
import CatchErrors from 'components/common/catch-errors';
import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Button, ButtonGroup } from 'react-bootstrap';
import { Ibox } from 'enspire-manager-framework';
import { PERMISSIONS } from 'components/constants';
import { Route, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { Table } from 'em-table';
import { imageSize } from 'components/common/toolbox';
import { useDispatch, useSelector } from 'react-redux';

const ExpiringDocsTab = (props) => {

    // props.id - id for attachment doc
    // props.source - path to doc e.g. 'assets' to read/write '[path]/[path]/[id]' 
    // props.prefix - prefix for Action Type e.g. [PREFIX]_ATTACHMENTS
    // props.field - for changing from default "attachments"
    // props.store & props.singular for accessing store e.g. useSelector((store) => [store].[singular]);

	const seg = 2;
    const history = useHistory();
    const dispatch = useDispatch();
    const params = useParams();
	const routeLocation = useLocation();
    const pathname = routeLocation.pathname;

	const admin = useSelector((store) => store.admin);
    const assets = useSelector((store) => store.assets);
    const user_permissions = useSelector(store => store.users?.user_permissions);
    
    const field = (props.field) ? props.field : 'attachments';
    const attachments = assets.asset_expiring_docs ?? [];
    const defaultAttach = (props.defaultAttach) ? props.defaultAttach : [];

    const [ data, setData ] = useState([]);
    const [ galleryPhotos, setGalleryPhotos ] = useState([]);
    const [ tabView, setTabView ] = useState('list');

    useEffect(() => {
		dispatch(actions_attachments.subAttachments(params.handle, props.id, props.source, props.prefix, field));
		return () => {
			const unsubscribe = assets.asset_expiring_docs_unsubscribe;
			if (typeof unsubscribe == 'function') unsubscribe();
		};
	}, [params.handle, props.id, props.source]);

	useEffect(() => {
        var newPhotos = [];
        attachments.forEach((attach, i) => {
            if (attach.images) attach.images.forEach((image) => {
                newPhotos.push({ name: attach.name, ...image });
            });
        });
        const result = Object.groupBy(newPhotos, ({ name }) => name);
        setGalleryPhotos(result);

	}, [attachments]);

	useEffect(() => {
        var newData = [];
        let index = 0;
        
        // First show default attachments - If not exist, show placeholder --------------------

        defaultAttach.forEach((def) => {
            let found = false;
            attachments.forEach((attach, i) => {
                let color = 'black'
                let daysLeft = moment(attach.expiration?.toDate()).diff(moment(), 'days');
                if (daysLeft <= attach.alertThreshold) color = 'warning';
                if (daysLeft < 0) color = 'danger';

                if (attach.name == def.name) {
                    newData.push({ ...attach, 
                        expiration: <span className={`text-${color}`}>{moment(attach.expiration?.toDate()).format('MMM Do, YYYY')}</span>,
                        index: i,
                        isDefault: true, 
                        def: <i className="fas fa-check-circle fa-xl"></i>, 
                        images: (attach.images?.length) ? <i className="fa-solid fa-camera fa-xl" rol="button" role="button" onClick={ () => toggleLightbox(i) }></i> : null 
                    });
                    found = true;
                }
            });
            if (!found) {
                newData.push({ ...def, 
                    index: attachments.length + index,
                    isDefault: true, 
                    def: <i className="fa-regular fa-circle fa-xl"></i>,
                });
                index++;
            }
        });

        // Show remaining attachments --------------------------------------------------------

        attachments.forEach((attach, i) => {
            let existing = _.find(defaultAttach, { name: attach.name });
            if (!existing) {

                let color = 'black'
                let daysLeft = moment(attach.expiration?.toDate()).diff(moment(), 'days');
                if (daysLeft <= attach.alertThreshold) color = 'warning';
                if (daysLeft < 0) color = 'danger';

                newData.push({ ...attach, 
                    expiration: <span className={`text-${color}`}>{moment(attach.expiration?.toDate()).format('MMM Do, YYYY')}</span>,
                    index: i, 
                    images: (attach.images?.length) ? <i className="fa-solid fa-camera fa-xl" role="button" onClick={ () => toggleLightbox(i) }></i> : null 
                });
            }
        });

        setData(newData)
	}, [attachments, defaultAttach]);

    // Trigger lightbox when URL param changes ------------------------------------------------------------------

    useEffect(() => {
        const [ attachIndex, imageIndex] = params.form_id?.split(':').map((i) => parseInt(i)) || [];
        const attach = attachments[attachIndex];

        if (params.form_id != '0' && attach) {

            const images = attach.images?.map((image) => image.imageUrl.includes('.pdf')
                ?   { type: 'custom', content: toolbox.getEmbedCode(image.imageUrl), title: attach.name, description: image.caption }
                :   { src: imageSize(image.imageUrl, 'lg'), title: attach.name, description: image.caption }
            );
            dispatch(actions_admin.lightboxSources([ ...images ]));
            dispatch(actions_admin.lightboxIndex(imageIndex));
            dispatch(actions_admin.lightboxShow(true));
        } else {
            dispatch(actions_admin.lightboxShow(false));
        }
    }, [params.form_id]);
   
    /* Handlers --------------------------*/

    const handleAddNew = () => {
		let location = toolbox.modifyPath(pathname, seg+3, "attachment-form/0");
		history.push({ pathname: location });
    };

    /* Actions --------------------------*/
    
	const openForm = (item) => {
        let location = toolbox.modifyPath(pathname, seg+3, `attachment-form/${item.name}`);
        history.push({ pathname: location });
	};
    const toggleLightbox = (attachIndex, imageIndex) => {
        let location = toolbox.modifyPath(pathname, 5, `attach/${attachIndex}:${(imageIndex ?? 0)}`);
        history.push({ pathname: location });
    }

    /* Constants --------------------------*/

    const columns = [
        { name: '', field: 'def', type: 'jsx', width: 5 },
        { name: 'Name', field: 'name', width: 40},
        { name: 'Expiration', field: 'expiration', type: 'jsx', format: 'MMM Do, YYYY', width: 35 },
        { name: 'Docs', field: 'images', type: 'jsx', width: 10 },
        { name: '', field: 'id', type: 'button', button: { name: <i className="fa-regular fa-gear fa-lg text-muted"></i>, className: 'btn-default btn-xs float-right', callback: openForm }, width: 10 }
    ];

    var galleries = [];
    let index = 0;
    for (let name of Object.keys(galleryPhotos)) {
        galleries.push(<AttachementGallery name={ name } photos={ galleryPhotos[name] } toggleLightbox={ toggleLightbox } attachIndex={ index++ } />);
    }
   
    return (
        <>
            <Ibox 
                title={'Licenses / Documents'}
                button={(props.button) ? props.button : '+ Attachment'}
                button_callback={handleAddNew}
                button_disabled={!user_permissions[PERMISSIONS.ASSETS_EDIT.key]}
            >
                <ButtonGroup className="mb-3">
                    <Button variant={ (tabView == 'list') ? 'secondary' : 'default' } onClick={ () => setTabView('list') }>List View</Button>
                    <Button variant={ (tabView == 'gallery') ? 'secondary' : 'default' } onClick={ () => setTabView('gallery') }>Gallery View</Button>
                    { props.store == 'serviceRequests' &&
                        <Button variant={ (tabView == 'all') ? 'secondary' : 'default' } onClick={ () => setTabView('all') }>Work Orders Gallery</Button>
                    }
                </ButtonGroup>

                { tabView == 'list'
                    ?   <>
                            <h3>{ `${props.name} Attachments` }</h3>                         
                            <Table id="attachments-tab"
                                data={ data }
                                columns={columns}
                                on_focus={(target) => props.setFocused(target)}
                                style={{ minHeight: '330px' }}
                            />
                        </>
                    :   galleries
                }

                { admin.flutter_keyboard &&
                    <div style={{ paddingBottom: '55vh' }}></div>
                }
            </Ibox>

            <Route path={ "/:handle/:appId/:entity/:asset_id?/attachment-form/:attachment_name/:tab?"} render={(route_props) => (
                <CatchErrors modal={true}>
                    <AttachmentForm {...route_props}
                        defaultAttach={ props.defaultAttach }
                        expires={ props.expires }
                        field={ props.field } 
                        id={ props.id } 
                        invoice={ props.invoice }
                        permissions={{ canEdit: user_permissions[PERMISSIONS.ASSETS_EDIT.key] }}
                        prefix={ props.prefix } 
                        singular={ props.singular } 
                        source={ props.source } 
                        store={ props.store } 
                    />
                </CatchErrors>
            )} />

        </>
    );
};

export default ExpiringDocsTab;